import React, { useState, useEffect, useRef } from "react";

// ─── CONFIG ─────────────────────────────────────────
const CLOUD_NAME = "dhrppyewc";
const UPLOAD_PRESET = "portfolio_unsigned";
const CATEGORIES = ["UI/UX", "Graphic Design", "Branding", "Product Design", "Reels"];
const GRAD = "linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39)";
const SHEET_API_URL = "https://script.google.com/macros/s/AKfycbxnXAJfAUGK2RauSc7gUZ_fMSoOoZCZe5pHTkVi3qCPTwzQFEUzkvjwz8spKbDDU1OL/exec";

const PortfolioAdmin = () => {
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [uploadCat, setUploadCat] = useState("UI/UX");
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCat, setEditCat] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [loadingSheet, setLoadingSheet] = useState(true);
  const fileRef = useRef();
  const thumbFileRef = useRef();

  useEffect(() => {
    return () => { 
        if (preview) URL.revokeObjectURL(preview); 
        if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    };
  }, [preview, thumbPreview]);

  // ─── FETCH FROM GOOGLE SHEET ──────────────────────
  useEffect(() => {
    const fetchFromSheet = async () => {
      try {
        const res = await fetch(SHEET_API_URL);
        const json = await res.json();
        if (json.success) {
          const loaded = json.data
            .filter(row => row.id)
            .map(row => {
              let url = row.url;
              let mainUrl = row.url;
              if (row.url && row.url.includes("|||")) {
                  const parts = row.url.split("|||");
                  url = parts[0];
                  mainUrl = parts[1];
              }
              return {
                id: row.id,
                publicId: row.id,
                rawUrl: row.url,
                url,
                mainUrl,
                title: row.title,
                category: row.category,
                deleted: row.deleted === "TRUE" || row.deleted === true,
                createdAt: row.uploadedAt || Date.now(),
              };
            });
          setItems(loaded);
        }
      } catch (e) {
        console.error("Failed to load from sheet:", e);
      } finally {
        setLoadingSheet(false);
      }
    };
    fetchFromSheet();
  }, []);

  // ─── SHEET POST (fixed Content-Type) ─────────────
  const sheetPost = async (payload) => {
  try {
    console.log("Sending to sheet:", payload);

    const res = await fetch(SHEET_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text();

    console.log("Status:", res.status);
    console.log("Response:", text);

  } catch (e) {
    console.error("Sheet post failed:", e);
  }
};

  // ─── FILE HANDLER ─────────────────────────────────
  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const handleThumbFile = (f) => {
    if (!f) return;
    setThumbFile(f);
    if (thumbPreview) URL.revokeObjectURL(thumbPreview);
    setThumbPreview(URL.createObjectURL(f));
  };

  // ─── UPLOAD ───────────────────────────────────────
  const uploadToCloudinary = (fileToUpload) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder", `portfolio/${uploadCat.replace(/\s/g, "_")}`);
      formData.append("context", `title=${title}|category=${uploadCat}`);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`);

      if (fileToUpload === file) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
          };
      }

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error("Upload failed"));
        }
      };
      xhr.onerror = () => reject(new Error("Upload error"));
      xhr.send(formData);
    });
  };

  const handleUploadWithProgress = async () => {
    if (!file || !title.trim()) return;
    if (uploadCat !== "Graphic Design" && !thumbFile) {
        alert("Please select a thumbnail image too.");
        return;
    }

    setUploading(true);
    setProgress(0);

    try {
        let thumbData;
        if (uploadCat !== "Graphic Design" && thumbFile) {
            thumbData = await uploadToCloudinary(thumbFile);
        }
        const mainData = await uploadToCloudinary(file);
        
        const publicId = mainData.public_id;
        const mainUrl = mainData.secure_url || `https://res.cloudinary.com/${CLOUD_NAME}/${mainData.resource_type || 'image'}/upload/${publicId}`;
        let finalUrl = mainUrl;
        let thumbUrl = mainUrl;
        
        if (thumbData) {
            thumbUrl = thumbData.secure_url || `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${thumbData.public_id}`;
            finalUrl = thumbUrl + "|||" + mainUrl;
        }

        const newItem = {
          id: publicId,
          publicId,
          rawUrl: finalUrl,
          url: thumbUrl,
          mainUrl: mainUrl,
          title,
          category: uploadCat,
          deleted: false,
          createdAt: Date.now(),
        };

        await sheetPost({
          action: "add",
          id: publicId,
          url: finalUrl,
          title,
          category: uploadCat,
          deleted: false,
          uploadedAt: new Date().toISOString(),
        });

        setItems(prev => [newItem, ...prev]);
        setFile(null);
        setPreview(null);
        setThumbFile(null);
        setThumbPreview(null);
        setTitle("");
        setUploading(false);
        alert("Upload successful!");
    } catch (error) {
        console.error(error);
        alert("Upload failed");
        setUploading(false);
    }
  };

  // ─── SOFT DELETE ──────────────────────────────────
  const handleDelete = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, deleted: true } : i));
    await sheetPost({ action: "update", id: item.id, field: "deleted", value: "TRUE" });
  };

  // ─── RESTORE ──────────────────────────────────────
  const handleRestore = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, deleted: false } : i));
    await sheetPost({ action: "update", id: item.id, field: "deleted", value: "FALSE" });
  };

  // ─── EDIT SAVE ────────────────────────────────────
  const handleEditSave = async (item) => {
    setItems(prev => prev.map(i =>
      i.id === item.id ? { ...i, title: editTitle, category: editCat } : i
    ));
    await sheetPost({ action: "update", id: item.id, field: "title", value: editTitle });
    await sheetPost({ action: "update", id: item.id, field: "category", value: editCat });
    setEditingId(null);
  };

  // ─── EMPTY TRASH (permanent delete) ──────────────
  const emptyTrash = async () => {
    const trashed = items.filter(i => i.deleted);
    if (!window.confirm(`Permanently delete ${trashed.length} items?`)) return;
    for (const item of trashed) {
      await sheetPost({ action: "delete", id: item.id });
    }
    setItems(prev => prev.filter(i => !i.deleted));
    alert("Trash emptied!");
  };

  // ─── IMAGE ERRORS ─────────────────────────────────
  const handleImageError = (id) => setImageErrors(prev => ({ ...prev, [id]: true }));
  const retryImage = (item) => {
    setImageErrors(prev => ({ ...prev, [item.id]: false }));
    setItems(prev => prev.map(i =>
      i.id === item.id
        ? { ...i, url: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${item.publicId}?t=${Date.now()}` }
        : i
    ));
  };

  // ─── FILTER ───────────────────────────────────────
  const filtered = activeTab === "Trash"
    ? items.filter(i => i.deleted)
    : items.filter(i => !i.deleted && (activeTab === "All" || i.category === activeTab));

  const activeCount = items.filter(i => !i.deleted).length;
  const trashCount = items.filter(i => i.deleted).length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Poppins:wght@300;400;500;600&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .pa-root { font-family: 'Poppins', sans-serif; min-height: 100vh; background: #0a0a0c; color: #f0f0f0; }
        .pa-header { padding: 24px 48px; border-bottom: 1px solid rgba(255,255,255,0.08); display: flex; justify-content: space-between; align-items: center; backdrop-filter: blur(10px); background: rgba(10,10,12,0.8); position: sticky; top: 0; z-index: 100; flex-wrap: wrap; gap: 16px; }
        .pa-logo { font-family: 'Space Grotesk', monospace; font-weight: 700; font-size: 1.5rem; background: ${GRAD}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; letter-spacing: -0.5px; }
        .header-actions { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
        .pa-stats { background: rgba(255,255,255,0.05); padding: 6px 14px; border-radius: 40px; font-size: 0.85rem; font-weight: 500; }
        .empty-trash-btn { background: rgba(244,90,57,0.2); border: 1px solid #F45A39; color: #F45A39; padding: 6px 14px; border-radius: 40px; font-size: 0.85rem; font-weight: 500; cursor: pointer; transition: all 0.2s; font-family: 'Poppins', sans-serif; }
        .empty-trash-btn:hover { background: rgba(244,90,57,0.4); transform: translateY(-1px); }
        .pa-body { max-width: 1400px; margin: 0 auto; padding: 40px 32px; }
        .upload-section { background: rgba(255,255,255,0.03); border-radius: 28px; padding: 28px 32px; margin-bottom: 48px; border: 1px solid rgba(255,255,255,0.06); backdrop-filter: blur(8px); }
        .upload-title { font-size: 1.3rem; font-weight: 600; margin-bottom: 20px; background: ${GRAD}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; display: inline-block; }
        .upload-grid { display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-end; }
        .upload-field { flex: 1; min-width: 180px; }
        .upload-field label { display: block; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; color: #aaa; margin-bottom: 8px; }
        .upload-field input, .upload-field select { width: 100%; background: rgba(20,20,24,0.9); border: 1px solid #2a2a2e; padding: 12px 16px; border-radius: 16px; color: white; font-family: 'Poppins', sans-serif; font-size: 0.9rem; }
        .upload-field input:focus, .upload-field select:focus { outline: none; border-color: #B55DD0; box-shadow: 0 0 0 2px rgba(181,93,208,0.2); }
        .file-label { background: rgba(255,255,255,0.05); border: 1px dashed #3a3a40; border-radius: 16px; padding: 12px 16px; text-align: center; cursor: pointer; transition: all 0.2s; color: #aaa; font-size: 0.9rem; }
        .file-label:hover { background: rgba(255,255,255,0.08); border-color: #B55DD0; }
        .preview-img { width: 64px; height: 64px; border-radius: 12px; object-fit: cover; border: 2px solid rgba(255,255,255,0.1); }
        .upload-btn { background: ${GRAD}; border: none; padding: 12px 28px; border-radius: 40px; font-weight: 600; color: white; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; font-family: 'Poppins', sans-serif; white-space: nowrap; }
        .upload-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.3); }
        .upload-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .tabs-container { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 32px; border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 16px; }
        .pa-tab { background: transparent; border: none; padding: 8px 24px; border-radius: 40px; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: 0.85rem; cursor: pointer; color: #aaa; transition: all 0.2s; }
        .pa-tab:hover { color: white; background: rgba(255,255,255,0.05); }
        .pa-tab.active { background: ${GRAD}; color: white; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
        .pa-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 28px; }
        .pa-card { background: rgba(20,20,24,0.7); border-radius: 24px; overflow: hidden; backdrop-filter: blur(4px); border: 1px solid rgba(255,255,255,0.06); transition: all 0.3s cubic-bezier(0.2,0.9,0.4,1.1); }
        .pa-card:hover { transform: translateY(-6px); border-color: rgba(181,93,208,0.3); box-shadow: 0 20px 30px -12px rgba(0,0,0,0.5); }
        .pa-card img { width: 100%; height: 250px; object-fit: cover; background: #0a0a0c; transition: transform 0.4s; display: block; }
        .pa-card:hover img { transform: scale(1.02); }
        .pa-card-body { padding: 18px; }
        .card-title { font-weight: 600; font-size: 1rem; margin-bottom: 6px; }
        .card-category { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; background: rgba(181,93,208,0.15); display: inline-block; padding: 4px 12px; border-radius: 40px; color: #D68BEF; margin-bottom: 16px; }
        .card-actions { display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap; }
        .card-actions button { background: rgba(255,255,255,0.05); border: none; padding: 8px 16px; border-radius: 40px; font-size: 0.75rem; font-weight: 500; cursor: pointer; color: #ddd; transition: all 0.2s; font-family: 'Poppins', sans-serif; }
        .card-actions button:hover { background: rgba(255,255,255,0.12); color: white; }
        .delete-btn:hover { background: rgba(244,90,57,0.2) !important; color: #F45A39 !important; }
        .restore-btn:hover { background: rgba(18,154,220,0.2) !important; color: #129ADC !important; }
        .edit-input, .edit-select { width: 100%; background: #2a2a2e; border: 1px solid #3a3a40; padding: 8px 12px; border-radius: 12px; color: white; margin-bottom: 10px; font-family: 'Poppins', sans-serif; font-size: 0.85rem; }
        .empty-state { text-align: center; padding: 80px 20px; color: #666; font-size: 0.95rem; }
        .image-error { background: #1a1a1e; height: 250px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #F45A39; font-size: 0.85rem; }
        .retry-btn { background: #129ADC; border: none; padding: 8px 20px; border-radius: 40px; color: white; cursor: pointer; font-weight: 500; font-family: 'Poppins', sans-serif; }
        .progress-bar { width: 100%; height: 4px; background: rgba(255,255,255,0.1); border-radius: 2px; overflow: hidden; margin-top: 8px; }
        .progress-fill { height: 100%; background: ${GRAD}; transition: width 0.3s; }
        .loading-state { text-align: center; padding: 80px 20px; color: #666; font-size: 0.95rem; }
        .sheet-badge { background: rgba(18,220,100,0.15); border: 1px solid rgba(18,220,100,0.3); color: #12dc64; padding: 4px 10px; border-radius: 40px; font-size: 0.72rem; font-weight: 500; }
        @media (max-width: 768px) {
          .pa-header { padding: 16px 24px; }
          .pa-body { padding: 24px 20px; }
          .upload-section { padding: 20px; }
          .upload-grid { flex-direction: column; gap: 16px; }
          .upload-btn { width: 100%; }
          .pa-grid { grid-template-columns: 1fr; }
          .pa-card img { height: 200px; }
        }
      `}</style>

      <div className="pa-root">
        {/* Header */}
        <div className="pa-header">
          <span className="pa-logo">✦ PORTFOLIO ADMIN</span>
          <div className="header-actions">
            <span className="sheet-badge">📊 Google Sheets</span>
            <div className="pa-stats">{activeCount} active • {trashCount} trashed</div>
            {trashCount > 0 && (
              <button className="empty-trash-btn" onClick={emptyTrash}>🗑 Empty Trash</button>
            )}
          </div>
        </div>

        <div className="pa-body">
          {/* Upload Section */}
          <div className="upload-section">
            <div className="upload-title">⬆ Add New Masterpiece</div>
            <div className="upload-grid">
               <div className="upload-field">
                <label>Category</label>
                <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>


              {uploadCat !== "Graphic Design" && (
                <div className="upload-field">
                  <label>Upload Thumbnail</label>
                  <div className="file-label" onClick={() => thumbFileRef.current?.click()}>
                    {thumbFile ? thumbFile.name : "Select thumbnail"}
                  </div>
                  <input ref={thumbFileRef} type="file" accept="image/*"
                    onChange={(e) => handleThumbFile(e.target.files[0])} style={{ display: "none" }} />
                </div>
              )}

              {uploadCat !== "Graphic Design" && (
                <div className="upload-field" style={{ flex: 'none', minWidth: '80px' }}>
                  <label>Thumb Preview</label>
                  {thumbPreview
                    ? <img src={thumbPreview} className="preview-img" alt="thumb preview" />
                    : <div style={{ width: 64, height: 64, background: "#1a1a1e", borderRadius: 12 }} />}
                </div>
              )}

              <div className="upload-field">
                <label>{uploadCat !== "Graphic Design" ? "Main Image / Video" : "Image"}</label>
                <div className="file-label" onClick={() => fileRef.current?.click()}>
                  {file ? file.name : "Click to select file"}
                </div>
                <input ref={fileRef} type="file" accept="image/*,video/*"
                  onChange={(e) => handleFile(e.target.files[0])} style={{ display: "none" }} />
              </div>

             



              <div className="upload-field" style={{ flex: 'none', minWidth: '80px' }}>
                <label>Preview</label>
                {preview
                  ? (file && file.type.startsWith("video/") 
                      ? <video src={preview} className="preview-img" muted loop autoPlay playsInline /> 
                      : <img src={preview} className="preview-img" alt="preview" />)
                  : <div style={{ width: 64, height: 64, background: "#1a1a1e", borderRadius: 12 }} />}
              </div>

               <div className="upload-field">
                <label>Title</label>
                <input placeholder="Enter title" value={title}
                  onChange={(e) => setTitle(e.target.value)} />
              </div>

              <div>
                <button className="upload-btn" onClick={handleUploadWithProgress}
                  disabled={uploading || !file || !title.trim() || (uploadCat !== "Graphic Design" && !thumbFile)}>
                  {uploading ? `✨ Uploading ${progress}%` : "✨ Upload"}
                </button>
                {uploading && (
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs-container">
            {["All", ...CATEGORIES, "Trash"].map(t => (
              <button key={t} className={`pa-tab ${activeTab === t ? "active" : ""}`}
                onClick={() => setActiveTab(t)}>
                {t}{t === "Trash" && trashCount > 0 ? ` (${trashCount})` : ""}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loadingSheet ? (
            <div className="loading-state">📊 Loading from Google Sheets...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">🧘 No items here. Upload something beautiful.</div>
          ) : (
            <div className="pa-grid">
              {filtered.map(item => (
                <div key={item.id} className="pa-card">
                  {imageErrors[item.id] ? (
                    <div className="image-error">
                      <span>⚠️ Failed to load image</span>
                      <button className="retry-btn" onClick={() => retryImage(item)}>🔄 Retry</button>
                    </div>
                  ) : (
                    <img src={item.url} alt={item.title}
                      onError={() => handleImageError(item.id)} loading="lazy" />
                  )}
                  <div className="pa-card-body">
                    {editingId === item.id ? (
                      <>
                        <input className="edit-input" value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)} />
                        <select className="edit-select" value={editCat}
                          onChange={(e) => setEditCat(e.target.value)}>
                          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                        <div className="card-actions">
                          <button onClick={() => handleEditSave(item)}>💾 Save</button>
                          <button onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="card-title">{item.title}</div>
                        <div className="card-category">{item.category}</div>
                        <div className="card-actions">
                          {activeTab === "Trash" ? (
                            <button className="restore-btn" onClick={() => handleRestore(item)}>
                              ↺ Restore
                            </button>
                          ) : (
                            <>
                              <button onClick={() => {
                                setEditCat(item.category || CATEGORIES[0]);
                                setEditingId(item.id);
                                setEditTitle(item.title || "");
                              }}>✎ Edit</button>
                              <button className="delete-btn" onClick={() => handleDelete(item)}>
                                🗑 Delete
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PortfolioAdmin;